import { useMemo } from 'react';
import TableCellWithoutPropertyDefinition from 'framework-listing-lib/internal/Table/components/TableCellWithoutPropertyDefinition';
import { useListingLibBulkActions } from 'framework-listing-lib/internal/Table/providers/ListingLibBulkActionsProvider';
import { useTableColumn } from 'framework-listing-lib/internal/Table/providers/TableColumnsProvider';
import { useHasInlineEdit } from 'framework-listing-lib/internal/frameworkAppSettings/hooks/useFrameworkAppSettings';
import { useListing } from 'framework-listing-lib/internal/providers/ListingProvider';
import { getPropertyValue } from 'framework-listing-lib/utils/crmObject';
import ListingLibInlineEditCell from './ListingLibInlineEditCell';
import ListingLibPropertyCell from './ListingLibPropertyCell';
import { Td } from './StyledComponents';
import { jsx as _jsx } from "react/jsx-runtime";
function ListingLibTableCellInner({
  column,
  columnName,
  columnNamespace,
  requiredCellProps,
  row
}) {
  const {
    enableObjectLevelPermissions
  } = useListing();
  const {
    getNestedLevel
  } = useListingLibBulkActions();
  const hasInlineEdit = useHasInlineEdit();
  const tdProps = useMemo(() => ({
    'aria-label': column.propertyDescriptor ? column.propertyDescriptor.label : '',
    'data-test-col-index': column.order,
    'data-test-id': 'crm-table-cell',
    'data-test-is-nested': getNestedLevel(row.objectId) > 0,
    'data-test-property-name': column.id,
    'data-test-object-id': `${row.objectId}`,
    'data-test-hover-cell': Boolean(column.hoverCell),
    tabIndex: 0,
    scope: column.order === 0 ? 'row' : undefined,
    width: column.width
  }), [column.hoverCell, column.id, column.order, column.propertyDescriptor, column.width, getNestedLevel, row.objectId]);
  const isInlineEditable = useMemo(() => {
    if (!column || !hasInlineEdit || column.order === 0) {
      return false;
    }

    // allow consumers to ignore row.currentUserPermissions?.canEdit if they want to
    if (column.getIsCellInlineEditable) {
      return column.getIsCellInlineEditable({
        crmObject: row,
        propertyName: column.id,
        propertyDefinition: column.propertyDescriptor,
        tdProps
      });
    }
    if (enableObjectLevelPermissions && row.currentUserPermissions) {
      return row.currentUserPermissions.canEdit;
    }
    return true;
  }, [column, enableObjectLevelPermissions, hasInlineEdit, row, tdProps]);

  /**
   * Disabling inline editing for the first column of the table
   * as it is usually used to link to a record details page and it contains
   * some extra content within the cell.
   */
  const CellComponent = isInlineEditable ? ListingLibInlineEditCell : ListingLibPropertyCell;
  return /*#__PURE__*/_jsx(CellComponent, {
    column: column,
    columnName: columnName,
    columnNamespace: columnNamespace,
    requiredCellProps: requiredCellProps,
    row: row,
    tdProps: tdProps
  });
}
export default function ListingLibTableCell({
  columnName,
  columnNamespace,
  requiredCellProps,
  row
}) {
  const column = useTableColumn(columnName);
  if (!column) {
    return /*#__PURE__*/_jsx(Td, Object.assign({}, requiredCellProps, {
      "data-test-id": "listing-lib-cell-without-column",
      children: /*#__PURE__*/_jsx(TableCellWithoutPropertyDefinition, {
        value: ""
      })
    }));
  }
  if (!column.propertyDescriptor) {
    return /*#__PURE__*/_jsx(Td, Object.assign({}, requiredCellProps, {
      "data-test-id": "listing-lib-cell-without-property-definition",
      children: /*#__PURE__*/_jsx(TableCellWithoutPropertyDefinition, {
        value: getPropertyValue(row, column.id)
      })
    }));
  }
  return /*#__PURE__*/_jsx(ListingLibTableCellInner, {
    column: column,
    columnName: columnName,
    columnNamespace: columnNamespace,
    requiredCellProps: requiredCellProps,
    row: row
  });
}